import { AirspaceType, isKnownAirspaceType, KNOWN_AIRSPACE_TYPES } from '@/domain/protocols/Airspaces'

import SymbolAdiz from '@/presentation/assets/airspaceSymbols/symbol-adiz.png'
import SymbolAfis from '@/presentation/assets/airspaceSymbols/symbol-afis.png'
import SymbolAtz from '@/presentation/assets/airspaceSymbols/symbol-atz.png'
import SymbolCta from '@/presentation/assets/airspaceSymbols/symbol-cta.png'
import SymbolCtr from '@/presentation/assets/airspaceSymbols/symbol-ctr.png'
import SymbolDangerous from '@/presentation/assets/airspaceSymbols/symbol-dangerous.png'
import SymbolFca from '@/presentation/assets/airspaceSymbols/symbol-fca.png'
import SymbolFcaAd from '@/presentation/assets/airspaceSymbols/symbol-fca-ad.png'
import SymbolFcaReh from '@/presentation/assets/airspaceSymbols/symbol-fca-reh.png'
import SymbolFir from '@/presentation/assets/airspaceSymbols/symbol-fir.png'
import SymbolFis from '@/presentation/assets/airspaceSymbols/symbol-fis.png'
import SymbolFiz from '@/presentation/assets/airspaceSymbols/symbol-fiz.png'
import SymbolFreeRouteAreas from '@/presentation/assets/airspaceSymbols/symbol-free-route-areas.png'
import SymbolProhibited from '@/presentation/assets/airspaceSymbols/symbol-prohibited.png'
import SymbolRestricted from '@/presentation/assets/airspaceSymbols/symbol-restricted.png'
import SymbolTma from '@/presentation/assets/airspaceSymbols/symbol-tma.png'
import SymbolUnclassified from '@/presentation/assets/airspaceSymbols/symbol-unclassified.png'

/**
 * Tudo que o mapa precisa para desenhar uma família de airspace: a cor de
 * linha/preenchimento, a cor do texto do rótulo, o nome da imagem Mapbox da
 * "pílula" do rótulo e os ajustes de posicionamento do rótulo. Esta é a fonte
 * única de estilo por tipo — os componentes de mapa e o presenter a leem em vez
 * de manter tabelas de cor/símbolo paralelas.
 */
export interface AirspaceTypeDisplay {
  /** Cor de linha + preenchimento (hex). */
  color: string
  /** Cor do texto sobre a pílula (hex). */
  textColor: string
  /** Nome da imagem Mapbox da pílula; uma chave de {@link AIRSPACE_SYMBOL_IMAGES}. */
  symbol: string
  /** Zoom mínimo em que o rótulo aparece. */
  labelMinLevel: number
  /** Offset latitudinal (graus) aplicado a posições de rótulo calculadas (center-of-mass). */
  labelYOffset: number
  /** Arredondamento decimal usado para deduplicar rótulos calculados sobrepostos. */
  labelSeparationDecimals: number
}

// Nomes de imagem Mapbox -> PNGs das pílulas de rótulo. Setores reusam a pílula
// do pai (firSector→fir, tmaSector→tma, ctaSector→cta), então o conjunto é
// chaveado por nome de pílula, não por tipo de airspace. `unclassified` cobre
// qualquer tipo fora do registry conhecido (ver UNCLASSIFIED_DISPLAY).
export const AIRSPACE_SYMBOL_IMAGES: Record<string, string> = {
  adiz: SymbolAdiz,
  afis: SymbolAfis,
  atz: SymbolAtz,
  cta: SymbolCta,
  ctr: SymbolCtr,
  fir: SymbolFir,
  fis: SymbolFis,
  fiz: SymbolFiz,
  'free-route-areas': SymbolFreeRouteAreas,
  fca: SymbolFca,
  'fca-reh': SymbolFcaReh,
  'fca-ad': SymbolFcaAd,
  dangerous: SymbolDangerous,
  prohibited: SymbolProhibited,
  restricted: SymbolRestricted,
  tma: SymbolTma,
  unclassified: SymbolUnclassified
}

const KNOWN_AIRSPACE_DISPLAY: Record<AirspaceType, AirspaceTypeDisplay> = {
  adiz: {
    color: '#6B7E33',
    textColor: '#FFFFFF',
    symbol: 'adiz',
    labelMinLevel: 22,
    labelYOffset: 0,
    labelSeparationDecimals: 1
  },
  afis: {
    color: '#720EA0',
    textColor: '#FFFFFF',
    symbol: 'afis',
    labelMinLevel: 6,
    labelYOffset: 0.02,
    labelSeparationDecimals: 1
  },
  atz: {
    color: '#34ADFF',
    textColor: '#000000',
    symbol: 'atz',
    labelMinLevel: 8,
    labelYOffset: 0.02,
    labelSeparationDecimals: 1
  },
  cta: {
    color: '#651FFF',
    textColor: '#FFFFFF',
    symbol: 'cta',
    labelMinLevel: 6,
    labelYOffset: 0,
    labelSeparationDecimals: 1
  },
  ctaSector: {
    color: '#651FFF',
    textColor: '#FFFFFF',
    symbol: 'cta',
    labelMinLevel: 6,
    labelYOffset: 0,
    labelSeparationDecimals: 1
  },
  ctr: {
    color: '#EB01EB',
    textColor: '#000000',
    symbol: 'ctr',
    labelMinLevel: 6,
    labelYOffset: -0.02,
    labelSeparationDecimals: 1
  },
  fir: {
    color: '#08090B',
    textColor: '#FFFFFF',
    symbol: 'fir',
    labelMinLevel: 0,
    labelYOffset: 0,
    labelSeparationDecimals: 1
  },
  firSector: {
    color: '#08090B',
    textColor: '#FFFFFF',
    symbol: 'fir',
    labelMinLevel: 6,
    labelYOffset: 0,
    labelSeparationDecimals: 1
  },
  fis: {
    color: '#FF1269',
    textColor: '#FFFFFF',
    symbol: 'fis',
    labelMinLevel: 5,
    labelYOffset: 0.13,
    labelSeparationDecimals: 1
  },
  fiz: {
    color: '#663E0E',
    textColor: '#FFFFFF',
    symbol: 'fiz',
    labelMinLevel: 6,
    labelYOffset: 0.02,
    labelSeparationDecimals: 1
  },
  freeRouteAreas: {
    color: '#1E7B6C',
    textColor: '#FFFFFF',
    symbol: 'free-route-areas',
    labelMinLevel: 1,
    labelYOffset: 0,
    labelSeparationDecimals: 1
  },
  fca: {
    color: '#42841F',
    textColor: '#FFFFFF',
    symbol: 'fca',
    labelMinLevel: 6,
    labelYOffset: 0,
    labelSeparationDecimals: 1
  },
  fcaReh: {
    color: '#14759A',
    textColor: '#FFFFFF',
    symbol: 'fca-reh',
    labelMinLevel: 6,
    labelYOffset: 0,
    labelSeparationDecimals: 1
  },
  fcaAd: {
    color: '#700D6A',
    textColor: '#FFFFFF',
    symbol: 'fca-ad',
    labelMinLevel: 6,
    labelYOffset: 0.02,
    labelSeparationDecimals: 1
  },
  suaDangerous: {
    color: '#FFA903',
    textColor: '#000000',
    symbol: 'dangerous',
    labelMinLevel: 8,
    labelYOffset: 0,
    labelSeparationDecimals: 1
  },
  suaProhibited: {
    color: '#FA1A1A',
    textColor: '#000000',
    symbol: 'prohibited',
    labelMinLevel: 9,
    labelYOffset: 0,
    labelSeparationDecimals: 2
  },
  suaRestricted: {
    color: '#A001AE',
    textColor: '#FFFFFF',
    symbol: 'restricted',
    labelMinLevel: 7.5,
    labelYOffset: 0,
    labelSeparationDecimals: 1
  },
  tma: {
    color: '#1239DF',
    textColor: '#FFFFFF',
    symbol: 'tma',
    labelMinLevel: 6,
    labelYOffset: 0,
    labelSeparationDecimals: 1
  },
  tmaSector: {
    color: '#1239DF',
    textColor: '#FFFFFF',
    symbol: 'tma',
    labelMinLevel: 6,
    labelYOffset: 0,
    labelSeparationDecimals: 1
  }
}

/**
 * Estilo para qualquer tipo de airspace que o app ainda não conhece (uma nova
 * classificação AIS trazida no dataset). Renderizado num cinza-ardósia neutro
 * para ler como "não catalogado" e não ser confundido com uma cor semântica, e
 * rotulado pelo tipo cru em maiúsculas (ver {@link getAirspaceMenuLabel}).
 */
export const UNCLASSIFIED_DISPLAY: AirspaceTypeDisplay = {
  color: '#5B6670',
  textColor: '#FFFFFF',
  symbol: 'unclassified',
  labelMinLevel: 6,
  labelYOffset: 0,
  labelSeparationDecimals: 1
}

/** Config de exibição de um tipo, com fallback para o estilo não catalogado. */
export const getAirspaceDisplay = (type: string): AirspaceTypeDisplay =>
  isKnownAirspaceType(type) ? KNOWN_AIRSPACE_DISPLAY[type] : UNCLASSIFIED_DISPLAY

/** Rótulo de menu/legenda de um tipo desconhecido: o tipo cru, em maiúsculas. */
export const getAirspaceMenuLabel = (type: string): string => type.toUpperCase()

// Uma chave de tipo pode ser uma string crua do dataset (espaços/barras/caixa);
// ids de source/layer do Mapbox e keys do React precisam de um slug estável.
export const slugifyAirspaceType = (type: string): string => type.toLowerCase().replace(/[^a-z0-9]+/g, '-')

// A ordem de renderização no mapa (= z-order): famílias conhecidas primeiro na
// sua ordem fixa, depois quaisquer tipos não catalogados que o dataset trouxe
// (a ordem de getAvailableTypes() é indefinida, então não deve dirigir o
// empilhamento).
export const orderAirspaceTypesForRender = (availableTypes: string[]): string[] => [
  ...KNOWN_AIRSPACE_TYPES,
  ...availableTypes.filter((type) => !isKnownAirspaceType(type))
]
